import { useNavigate } from '@tanstack/react-router';
import {
  CompanyInventory,
  CompanyProducts,
  InventoryUnitsProvider,
  type InventoryCapabilities,
} from '../modules/inventory/index.js';
import { useCompanyScope } from './company-context.js';
import { GatedCompanyScreen } from './gated-company-screen.js';
import type { InventoryView, ProductsView } from './list-views.js';
import type { ViewSetter } from './view-state.js';

/// O que a pessoa pode fazer no Estoque e em Produtos, que são do mesmo módulo.
function useInventoryAccess() {
  const { can, timeZone, company } = useCompanyScope();
  const navigate = useNavigate();
  const units = company.businessUnits ?? [];
  // Visão consolidada lê as unidades somadas; movimentar acontece dentro de uma unidade (ADR 0039).
  const consolidated = units.length > 1 && (company.activeBusinessUnitId ?? null) === null;
  const capabilities: InventoryCapabilities = {
    canManageProducts: can('inventory.products.manage'),
    canManageLocations: can('inventory.locations.manage'),
    canRegisterMovements: !consolidated && can('inventory.movements.create'),
    canReverseMovements: !consolidated && can('inventory.movements.reverse'),
    canManageReplenishment: !consolidated && can('inventory.replenishment.manage'),
    /// Abre a venda buscando o produto; com uma só apresentação à venda, ela já entra no
    /// carrinho pelo preço que vale hoje (ADR 0048).
    onSell: can('sales.create')
      ? (product) => {
          const priced = product.packages.filter(
            (item) => item.active && item.salePriceCents !== null,
          );
          void navigate({
            to: '/companies/$companyKey/sales/new',
            params: { companyKey: company.key },
            search: {
              search: product.displayName,
              ...(priced.length === 1 ? { sell: `PRODUCT:${priced[0]!.id}` } : {}),
            },
          });
        }
      : undefined,
  };
  return {
    allowed: can('inventory.read'),
    timeZone,
    units: {
      showUnits: units.length > 1,
      consolidated,
      options: units.map((unit) => ({ value: unit.id, label: unit.displayName })),
      activeBusinessUnitId: company.activeBusinessUnitId,
    },
    capabilities,
  };
}

export function CompanyInventoryScreen({
  view,
  onView,
}: {
  view: InventoryView;
  onView: ViewSetter<InventoryView>;
}) {
  const access = useInventoryAccess();
  return (
    <GatedCompanyScreen allowed={access.allowed}>
      <InventoryUnitsProvider value={access.units}>
        <CompanyInventory
          capabilities={access.capabilities}
          timeZone={access.timeZone}
          view={view}
          onView={onView}
        />
      </InventoryUnitsProvider>
    </GatedCompanyScreen>
  );
}

/// Produtos no Catálogo (ADR 0055): mesma porta e mesmas capacidades do Estoque.
export function CompanyProductsScreen({
  view,
  onView,
}: {
  view: ProductsView;
  onView: ViewSetter<ProductsView>;
}) {
  const access = useInventoryAccess();
  return (
    <GatedCompanyScreen allowed={access.allowed}>
      <InventoryUnitsProvider value={access.units}>
        <CompanyProducts
          capabilities={access.capabilities}
          timeZone={access.timeZone}
          view={view}
          onView={onView}
        />
      </InventoryUnitsProvider>
    </GatedCompanyScreen>
  );
}
